/**
 * Linked Options Filter
 * Filters second option (Cannage) based on first option (Matériau) selection
 * Only shows cannage values that have available variants for the selected material
 * Supports both radio button pickers and dropdown pickers
 *
 * Handles DOM replacement by the theme (via HTMLUpdateUtility.viewTransition)
 */

(function() {
  'use strict';

  let availableMap = null;
  let pendingTimeouts = [];
  let isInitialized = false;
  let lastFilterSignature = null;
  let isFiltering = false;
  let mutationObserverRef = null;

  // Inject CSS for hiding options
  function injectStyles() {
    if (document.getElementById('linked-options-styles')) return;
    const style = document.createElement('style');
    style.id = 'linked-options-styles';
    style.textContent = `
      .variant-picker__option--linked-hidden {
        display: none !important;
        visibility: hidden !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
        position: absolute !important;
        pointer-events: none !important;
      }
      /* For dropdown options */
      option.variant-picker__option--linked-hidden,
      .variant-picker__option.variant-picker__option--linked-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function initLinkedOptionsFilter() {
    injectStyles();

    const picker = document.querySelector('[data-variant-picker]');
    if (!picker) {
      console.log('[LinkedOptions] No variant picker found');
      return;
    }

    // Check if first option is Matériau
    const fields = picker.querySelectorAll('.variant-picker__field');
    if (fields.length < 2) {
      console.log('[LinkedOptions] Less than 2 variant fields found');
      return;
    }

    const firstOptionField = fields[0];
    const firstOptionName = firstOptionField.dataset.optionName;

    if (!firstOptionName || !['matériau', 'material'].includes(firstOptionName.toLowerCase())) {
      console.log('[LinkedOptions] First option is not Matériau, skipping');
      return;
    }

    // Build available map if not already done
    if (!availableMap) {
      const productId = picker.dataset.productId;
      console.log('[LinkedOptions] Initializing for product', productId);

      const productJsonScript = document.querySelector('[data-product-json]');
      let variants = [];

      if (productJsonScript) {
        try {
          const productData = JSON.parse(productJsonScript.textContent);
          variants = productData.variants || [];
        } catch (e) {
          console.warn('[LinkedOptions] Could not parse product JSON:', e);
        }
      }

      if (variants.length === 0) {
        const handle = picker.dataset.productHandle;
        if (handle) {
          fetchProductVariants(handle).then(fetchedVariants => {
            if (fetchedVariants.length > 0) {
              buildAvailableMap(fetchedVariants);
              setupListeners();
              runFilter();
            }
          });
        }
        return;
      }

      buildAvailableMap(variants);
    }

    if (!isInitialized) {
      setupListeners();
      isInitialized = true;
    }

    // Run filter immediately
    runFilter();
  }

  function buildAvailableMap(variants) {
    availableMap = new Map();

    variants.forEach(variant => {
      const option1 = variant.option1;
      const option2 = variant.option2;

      if (!option1 || !option2) return;

      if (!availableMap.has(option1)) {
        availableMap.set(option1, new Set());
      }
      availableMap.get(option1).add(option2);
    });

    console.log('[LinkedOptions] Available map built:', Object.fromEntries(
      Array.from(availableMap.entries()).map(([k, v]) => [k, Array.from(v)])
    ));
  }

  async function fetchProductVariants(handle) {
    try {
      if (!handle) return [];
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      return product.variants || [];
    } catch (e) {
      console.warn('[LinkedOptions] Could not fetch product variants:', e);
      return [];
    }
  }

  function setupListeners() {
    // Listen for change events on the variant picker (handles both radio and select)
    document.addEventListener('change', function(e) {
      const input = e.target;
      const field = input.closest('.variant-picker__field');
      if (!field) return;

      const optionName = field.dataset.optionName;
      if (optionName && ['matériau', 'material'].includes(optionName.toLowerCase())) {
        console.log('[LinkedOptions] Matériau changed, scheduling filter');
        lastFilterSignature = null;
        scheduleFilter();
      }
    });

    // Observe the product-info element for DOM replacements (theme re-renders variant picker)
    // Ignore mutations caused by our own filter (class/style changes on options)
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      mutationObserverRef = new MutationObserver((mutations) => {
        if (isFiltering) return;

        let shouldSchedule = false;
        for (const mutation of mutations) {
          if (mutation.type !== 'childList') continue;
          const hasAddedOrRemoved = mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
          if (!hasAddedOrRemoved) continue;

          const target = mutation.target;
          if (target && target.closest && target.closest('.variant-picker__option-values')) {
            continue;
          }
          shouldSchedule = true;
          break;
        }

        if (shouldSchedule) {
          // DOM was replaced: the previous signature no longer applies
          lastFilterSignature = null;
          scheduleFilter();
        }
      });

      mutationObserverRef.observe(productInfo, {
        childList: true,
        subtree: true
      });

      console.log('[LinkedOptions] MutationObserver started on product-info');
    }

    // Listen for theme's variant change event
    if (window.FoxThemeEvents && window.FoxThemeEvents.subscribe) {
      window.FoxThemeEvents.subscribe('variant-change', () => {
        console.log('[LinkedOptions] Theme variant-change event received');
        lastFilterSignature = null;
        scheduleFilter();
      });
    }

    // Also listen for custom variant:changed event
    document.addEventListener('variant:changed', () => {
      console.log('[LinkedOptions] variant:changed event received');
      lastFilterSignature = null;
      scheduleFilter();
    });
  }

  function scheduleFilter() {
    pendingTimeouts.forEach(clearTimeout);
    pendingTimeouts = [];

    runFilter();

    // One follow-up pass to catch late theme re-renders, then stop.
    pendingTimeouts.push(setTimeout(() => {
      pendingTimeouts = [];
      runFilter();
    }, 250));
  }

  function runFilter() {
    if (!availableMap) {
      console.log('[LinkedOptions] No available map yet');
      return;
    }

    const picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;

    const fields = picker.querySelectorAll('.variant-picker__field');
    if (fields.length < 2) return;

    const firstOptionField = fields[0];
    const secondOptionField = fields[1];

    // Get current first option value (handle both radio and select)
    let selectedFirstValue = null;
    const checkedFirst = firstOptionField.querySelector('input[type="radio"]:checked');
    if (checkedFirst) {
      selectedFirstValue = checkedFirst.value;
    } else {
      const selectFirst = firstOptionField.querySelector('select');
      if (selectFirst) {
        selectedFirstValue = selectFirst.value;
      }
    }

    if (!selectedFirstValue) {
      console.log('[LinkedOptions] No material selected yet');
      return;
    }

    const availableSecondValues = availableMap.get(selectedFirstValue) || new Set();

    // Dedup: if the signature hasn't changed since last applied, skip entirely
    const signature = selectedFirstValue + '|' + Array.from(availableSecondValues).sort().join(',');
    if (signature === lastFilterSignature) {
      return;
    }
    lastFilterSignature = signature;

    console.log('[LinkedOptions] Filtering for material:', selectedFirstValue,
                '| Available cannage values:', Array.from(availableSecondValues));

    isFiltering = true;
    let hiddenCount = 0;
    let shownCount = 0;

    // Handle radio button picker
    const secondOptionContainer = secondOptionField.querySelector('.variant-picker__option-values');
    if (secondOptionContainer) {
      const allSecondOptions = secondOptionContainer.querySelectorAll('.variant-picker__option');
      console.log('[LinkedOptions] Found', allSecondOptions.length, 'radio options to filter');

      allSecondOptions.forEach(optionEl => {
        const input = optionEl.querySelector('input[type="radio"]');
        if (!input) return;

        const optionValue = input.value;
        const isAvailable = availableSecondValues.has(optionValue);

        if (isAvailable) {
          optionEl.classList.remove('variant-picker__option--linked-hidden');
          optionEl.style.cssText = optionEl.style.cssText.replace(/display:\s*none\s*!important;?/gi, '');
          shownCount++;
        } else {
          optionEl.classList.add('variant-picker__option--linked-hidden');
          // Use both class and inline style for maximum reliability
          if (!optionEl.style.display || optionEl.style.display !== 'none') {
            optionEl.style.setProperty('display', 'none', 'important');
          }
          hiddenCount++;
        }
      });
    }

    // Handle dropdown picker
    const secondSelect = secondOptionField.querySelector('select');
    if (secondSelect) {
      const allOptions = secondSelect.querySelectorAll('option');
      console.log('[LinkedOptions] Found', allOptions.length, 'dropdown options to filter');

      let firstAvailableOption = null;
      let currentSelectedStillAvailable = false;

      allOptions.forEach(option => {
        const optionValue = option.value;
        const isAvailable = availableSecondValues.has(optionValue);

        if (isAvailable) {
          option.classList.remove('variant-picker__option--linked-hidden');
          option.disabled = false;
          option.style.display = '';
          option.hidden = false;
          if (!firstAvailableOption) {
            firstAvailableOption = option;
          }
          if (option.selected) {
            currentSelectedStillAvailable = true;
          }
          shownCount++;
        } else {
          option.classList.add('variant-picker__option--linked-hidden');
          option.disabled = true;
          option.style.display = 'none';
          option.hidden = true;
          hiddenCount++;
        }
      });

      // If current selection is no longer available, select the first available option
      if (!currentSelectedStillAvailable && firstAvailableOption) {
        console.log('[LinkedOptions] Auto-selecting first available option:', firstAvailableOption.value);
        secondSelect.value = firstAvailableOption.value;
        // Trigger change event so the theme updates
        secondSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    console.log('[LinkedOptions] Filter complete:', shownCount, 'shown,', hiddenCount, 'hidden');

    // Release the mutation-observer guard on the next microtask so DOM mutations
    // triggered by our class/style changes are ignored by the observer.
    Promise.resolve().then(() => { isFiltering = false; });
  }

  // Initialize - use longer delay if there's a variant in URL to let Shopify set it first
  function getInitDelay() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('variant') ? 500 : 200;
  }

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(initLinkedOptionsFilter, getInitDelay());
    });
  } else {
    setTimeout(initLinkedOptionsFilter, getInitDelay());
  }

  // Handle theme section reloads (for theme editor)
  document.addEventListener('shopify:section:load', () => {
    availableMap = null;
    isInitialized = false;
    setTimeout(initLinkedOptionsFilter, 200);
  });

  // Re-run filter on page visibility change (in case user navigated away and back)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && availableMap) {
      setTimeout(runFilter, 100);
    }
  });
})();
